import { ChatBubbleTypingOvalIcon } from "icons";
import { usePathname } from "next/navigation";
import React, { useEffect, useState } from "react";
import { Button, ImageWrapper, Link, LinkButton } from "ui-components";

import { makeToast } from "@/components/Toast/Toast";
import { CookieKeys } from "@/enums/CookieKeys.enum";
import {
  UserResponseEntity,
  UserResponseEntityConnectionStatus,
  useUserRequestConnection,
} from "@/hooks/api/generated";
import { useAuth } from "@/hooks/useAuth";
import { useCookies } from "@/hooks/useCookies";
import { useNavigate } from "@/hooks/useNavigate";

export default function SpeakerCard({
  type = "hosts",
  speaker,
  refetch,
}: {
  type?: string;
  speaker: UserResponseEntity;
  refetch?: () => void;
}): JSX.Element {
  const { push } = useNavigate();
  const { setCookie, getCookie, removeCookie } = useCookies();
  const [isBioVisible, setIsBioVisible] = useState(false);
  const pathname = usePathname();
  const { authData } = useAuth();
  const { mutate: sendConnectionRequest, isPending } =
    useUserRequestConnection();
  const [isNewRequestSendSuccessfully, setIsNewRequestSendSuccessfully] =
    useState(false);
  const cleanLimitedString = (str: string, length: number): string => {
    // Slice by code points (Array.from), not UTF-16 units, so a multi-byte
    // char (e.g. an emoji in a name/pronoun) is never split into a lone
    // surrogate — which the server encodes as U+FFFD but the client keeps raw,
    // causing a hydration mismatch.
    const chars = Array.from(str ?? "");
    return chars.length > length
      ? `${chars.slice(0, length).join("")}...`
      : str;
  };

  function handleConnectClick(userId: number): void {
    sendConnectionRequest(
      {
        userId,
      },
      {
        onSuccess: () => {
          refetch?.();
          setIsNewRequestSendSuccessfully(true);
          makeToast({
            title: "Connection request sent successfully.",
            intent: "success",
          });
        },
      }
    );
  }

  function handleLoginClick(userId: string): void {
    setCookie({
      key: CookieKeys.SpeakerLogin,
      value: userId,
    });
    push(`/signin?redirect-to=${pathname}`);
  }

  function renderConnectButton({
    bgColor = "!bg-[#2B365F]",
    textColor = "",
  }): JSX.Element {
    if (authData) {
      if (
        speaker.connectionStatus === UserResponseEntityConnectionStatus.active
      ) {
        return (
          <LinkButton
            className={`w-[128px] ${
              type === "speakers"
                ? isBioVisible
                  ? "bg-white !text-[#282120]"
                  : "!text-[#282120] !bg-[#F4BF43]"
                : "text-[#F4BF43] !bg-[#524947]"
            }`}
            intent="secondary"
            href={`/profile/messages?recipient=${speaker.id}`}
          >
            Message
          </LinkButton>
        );
      }

      if (
        speaker.connectionStatus ===
          UserResponseEntityConnectionStatus.request ||
        isNewRequestSendSuccessfully
      ) {
        return (
          <Button className="w-[128px]" isDisabled intent="secondary">
            Invitation Sent
          </Button>
        );
      }

      return (
        <Button
          className={`w-[128px] ${bgColor} ${textColor}`}
          intent="secondary"
          isDisabled={isPending}
          isLoading={isPending}
          onClick={() => handleConnectClick(speaker.id)}
        >
          Connect
        </Button>
      );
    }

    return (
      <Button
        className={`w-[128px] ${bgColor} ${textColor}`}
        intent="secondary"
        onClick={() => handleLoginClick(String(speaker.id))}
      >
        Connect
      </Button>
    );
  }

  useEffect(() => {
    if (getCookie(CookieKeys.SpeakerLogin)) {
      setTimeout(() => {
        handleConnectClick(Number(getCookie(CookieKeys.SpeakerLogin)));
        setIsNewRequestSendSuccessfully(true);
        removeCookie(CookieKeys.SpeakerLogin);
        refetch?.();
      }, 2000);
    }
  }, [getCookie, removeCookie, handleConnectClick, CookieKeys]);

  return (
    <div
      className="group relative w-[295px] h-full mx-auto"
      onMouseLeave={() => setIsBioVisible(false)}
    >
      <div
        className={`absolute inset-0 transition-opacity duration-300 z-10 ${
          isBioVisible ? "opacity-0" : "opacity-100"
        }`}
      >
        <div className="w-full h-full p-6 bg-[#F2F2F2] rounded-2xl">
          {speaker?.userTaxonomy?.personal_message?.trim() && (
            <div className="cursor-pointer">
              <ChatBubbleTypingOvalIcon
                color={type === "speakers" ? "#282120" : "#282120"}
                className="absolute right-5 cursor-pointer"
                onClick={() => setIsBioVisible(true)}
                onMouseEnter={() => setIsBioVisible(true)}
              />
            </div>
          )}

          <div className=" flex flex-col justify-start items-center gap-7">
            <ImageWrapper
              src={
                speaker?.userTaxonomy?.image?.name
                  ? speaker.userTaxonomy.image.name
                  : "https://res.cloudinary.com/dv6hrcluu/image/upload/v1731336611/no_image_color_y7uarw.png"
              }
              fill
              style={{
                objectFit: "cover",
              }}
              className={`!relative !w-[200px] !h-[200px] rounded-full  border-[3px] border-solid ${
                type === "speakers" ? "border-[#F4BF43]" : "border-[#282120]"
              }`}
              alt={`${speaker?.first_name} ${speaker?.last_name}`}
            />
            <div className="w-[244px] flex flex-col justify-start items-center gap-3">
              <div className="w-full text-center text-black text-[20px] font-bold break-words">
                <Link
                  href={`/members/${speaker.slug}`}
                  hasHoverEffect={false}
                  className={`!text-[20px] !font-bold ${
                    type === "speakers" ? "!text-[#282120]" : "!text-[#282120]"
                  }`}
                  decoration={false}
                >
                  {cleanLimitedString(
                    `${speaker?.first_name} ${speaker?.last_name}`,
                    20
                  )}
                </Link>
              </div>
              <div className="w-full flex flex-col justify-start items-center gap-[5px] text-black text-sm font-medium text-center break-words h-[95px]">
                <div>{speaker?.pronoun}</div>
                {speaker?.type === "professional" ? (
                  <>
                    {speaker?.userTaxonomy?.job_title && (
                      <div>
                        {cleanLimitedString(
                          speaker?.userTaxonomy?.job_title,
                          33
                        )}
                      </div>
                    )}
                    <div>
                      {speaker.userTaxonomy.organization?.slug ? (
                        <Link
                          href={`/organizations/${speaker.userTaxonomy.organization.slug}`}
                          hasHoverEffect={false}
                          decoration={false}
                          className={`underline ${
                            type === "speakers" ? "" : "!text-[#282120]"
                          }`}
                        >
                          {speaker.userTaxonomy.organization.name}
                        </Link>
                      ) : (
                        speaker?.userTaxonomy?.organization_name || ""
                      )}
                    </div>
                  </>
                ) : (
                  <div>
                    {cleanLimitedString(
                      speaker?.userTaxonomy?.school || "",
                      33
                    )}
                  </div>
                )}
                <div>
                  {speaker.location ? speaker.location.location : ""}&nbsp;
                </div>
              </div>
            </div>
            {renderConnectButton({
              bgColor: `${
                type === "speakers" ? "!bg-[#F4BF43]" : "!bg-[#282120]"
              }`,
              textColor: `${
                type === "speakers" ? "!text-[#282120]" : "!text-[#F4BF43]"
              }`,
            })}
          </div>
        </div>
      </div>
      <div
        className={`transition-opacity duration-300 relative ${
          isBioVisible ? "opacity-100 z-20" : "opacity-0"
        }`}
      >
        <div
          className={`h-full px-5 py-6 rounded-2xl flex flex-col justify-start gap-4 ${
            type === "speakers" ? "bg-[#F4BF43]" : "bg-[#282120]"
          }`}
        >
          <div
            className={`${
              type === "speakers" ? "" : "text-[#F4BF43]"
            } text-[20px] font-bold break-words`}
          >
            <ChatBubbleTypingOvalIcon
              color={type === "speakers" ? "black" : "white"}
              className="absolute right-5 cursor-pointer"
            />
            Bio
          </div>
          <div
            className={`h-[323px] p-3 bg-white overflow-y-auto rounded-lg flex flex-col gap-[10px] text-sm font-medium scrollbar-thin ${
              type === "speakers" ? "text-[#282120]" : "text-[#282120]"
            }`}
            style={{
              scrollbarColor: `${
                type === "speakers" ? "#F4BF43" : "#282120"
              } transparent`,
              scrollbarWidth: "thin",
            }}
          >
            {speaker?.userTaxonomy?.personal_message}
          </div>
          <div className="flex flex-col justify-start items-center mt-2">
            {renderConnectButton({
              bgColor: "bg-white",
              textColor:
                type === "speakers" ? "!text-[#282120]" : "!text-[#282120]",
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
